import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Button,
	Box,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import EmoSupportListSummaryFilterSection from './components/EmoSupportListSummaryFilterSection';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import {
	useDeleteEmoMutation,
	useGetEmoListQuery,
} from '@/api/information/emoApiSlice';
import {
	EmoPageFilter,
	InformationEmotionSupportDTO,
} from '@/api/DTO/information/emo.interface';
import { showLabel } from '@/utils/helper';
import { useGetInformationTypeQuery } from '@/api/common';
import SetSubTitleDialog from './components/SetSubTitleDialog';
import { InformationType } from '@/api/enum/common.enum';
import SetOrderDialog from './components/SetOrderDialog';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

export interface emoListDataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function EmoSupportListSummaryPage() {
	const { t } = useTranslation();
	const showModal = useModal();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [deleteEmo] = useDeleteEmoMutation();
	const navigate = useNavigate();

	const defaultPropertyFilter: EmoPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<EmoPageFilter> = defaultPropertyFilter;
	const methods = useForm<EmoPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: EmoPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const typeList = [
		{ id: 0, name: t('emo.web') },
		{ id: 1, name: t('emo.pdf') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetEmoListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});
	const { data: informationTypeData } = useGetInformationTypeQuery(
		InformationType.moreEmo,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const transformResponse = useCallback(
		(response?: InformationEmotionSupportDTO[]) => {
			if (response == null) return;
			const newResponse: emoListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as EmoPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('emo.cn_title'),
			flex: 21,
		},
		{
			field: 'descriptionZh',
			headerName: t('emo.cn_description'),
			flex: 15,
		},
		{
			field: 'titleEn',
			headerName: t('emo.en_title'),
			flex: 21,
		},
		{
			field: 'descriptionEn',
			headerName: t('emo.en_description'),
			flex: 21,
		},
		{
			field: 'mediaType',
			headerName: t('emo.type'),
			flex: 21,
			renderCell: (params: any) => showLabel(params.row.mediaType, typeList),
		},
		{
			field: 'updatedAt',
			headerName: t('emo.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('emo.createTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/information-manage/emo-support-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton color='error' onClick={() => handleDelete(params.row.id)}>
					<DeleteOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleDelete = async (id: string) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('emo.deleteItem')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('emo.isDeleteItem')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteEmo(id);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteItemSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.emoSupportList')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/information-manage/emo-support-list/add`)}
					>
						{t('emo.addOptions')}
					</Button>
				</Stack>
			</Box>
			<Box display='flex' alignItems={'center'} marginTop={'12px'}>
				<Typography variant='body1'>
					{t('emo.subTitle')}：
					{informationTypeData?.data.subtitleZh
						? informationTypeData?.data.subtitleZh
						: informationTypeData?.data.subtitleEn
						? informationTypeData?.data.subtitleEn
						: '-'}
				</Typography>
				<SetSubTitleDialog
					informationTypeData={informationTypeData}
				></SetSubTitleDialog>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<EmoSupportListSummaryFilterSection></EmoSupportListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
